/**
 * Compact relative-time formatter for the agent-instance-picker dropdown
 * (Task 4.1, visual-critique non-blocking suggestion #2). Renders the per-row
 * recency cue as a short, single-token string at the right edge of each row.
 *
 * Distinct from `formatRelativeTime` below (which renders verbose
 * "2 hours ago" / "in 4 days" copy for the Members table). The picker needs a
 * narrow, fixed-ish-width slot — so we use abbreviated units and roll over to
 * a `Mon D` date once the diff exceeds a week.
 *
 * Buckets (locked v1 — see Task 4.1 phase plan):
 *   - `ts === null`            → "—"  (never-sent sentinel; constant width)
 *   - diff < 60s               → "just now"
 *   - diff < 1h                → "{n}m ago"  (1..59)
 *   - diff < 1d                → "{n}h ago"  (1..23)
 *   - diff < 7d                → "{n}d ago"  (1..6)
 *   - diff >= 7d               → toLocaleDateString(undefined, { month, day })
 *
 * Defensive: future timestamps (clock skew) collapse to "just now" rather than
 * "in 5m" — the picker has no concept of future activity, so any negative diff
 * is just noise.
 *
 * v1 has no debounce / "now bucket" memoization — every render recomputes.
 * Phase 4 plan flagged that as a future polish only if profiling shows hot.
 */
export function relativeTime(ts: number | null, now: number): string {
  if (ts === null) return '—';
  const diffMs = now - ts;
  if (diffMs < 60_000) return 'just now';
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMs / 3_600_000);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffMs / 86_400_000);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Render an ISO timestamp as a coarse relative-time string ("2 hours ago",
 * "3 days ago", "in 4 days"). Used by the Members table for `last_active_at`
 * + `invited_at` + `expires_at`. Server returns UTC ISO strings; we render
 * client-side at draw time. Acceptable staleness — no realtime requirement.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number = Date.now()
): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '—';
  const deltaSec = Math.round((ts - now) / 1000);
  const abs = Math.abs(deltaSec);
  const suffix = (n: number, unit: string) =>
    deltaSec < 0
      ? `${n} ${unit}${n === 1 ? '' : 's'} ago`
      : `in ${n} ${unit}${n === 1 ? '' : 's'}`;
  if (abs < 45) return deltaSec < 0 ? 'just now' : 'in a moment';
  if (abs < 90) return suffix(1, 'minute');
  if (abs < 3600) return suffix(Math.round(abs / 60), 'minute');
  if (abs < 5400) return suffix(1, 'hour');
  if (abs < 86_400) return suffix(Math.round(abs / 3600), 'hour');
  if (abs < 129_600) return suffix(1, 'day');
  if (abs < 2_592_000) return suffix(Math.round(abs / 86_400), 'day');
  if (abs < 5_184_000) return suffix(1, 'month');
  if (abs < 31_536_000) return suffix(Math.round(abs / 2_592_000), 'month');
  return suffix(Math.round(abs / 31_536_000), 'year');
}
